import React from 'react'

import { useI18n } from '@runtime/core/i18n/i18n'
import { getHelpCharacter, getHelpCharacterAlt } from '@/features/help/characters/helpCharacters'
import { Modal, ModalTitle } from '@runtime/components/ui/Modal'
import { Button } from '@runtime/components/ui/Button'
import { FormLine } from '@runtime/components/ui/Form'
import { Text } from '@runtime/components/ui/Text'
import styles from '@/features/help/components/CharacterHelpModal.module.scss'

/**
 * Help modal with a character avatar (coach/detective/etc.).
 * Extracted from the legacy inline React code in index.html.
 */
type CharacterHelpModalProps = {
  open: boolean
  character: string
  title?: string
  children: React.ReactNode
  onClose: () => void
  white?: boolean
  preAvatar?: React.ReactNode
  noTextWrap?: boolean
}

export default function CharacterHelpModal({
  open,
  character,
  title,
  children,
  onClose,
  white,
  preAvatar,
  noTextWrap,
}: CharacterHelpModalProps) {
  const { t } = useI18n()

  const char = getHelpCharacter(character)
  const contentClasses = [styles.modalContent, white ? styles.modalContentWhite : '']
    .filter(Boolean)
    .join(' ')
  const contentStyle: React.CSSProperties = white
    ? {
        background: '#ffffff',
        color: '#111',
        boxSizing: 'border-box',
        width: 'min(680px, 90vw)',
        maxWidth: '90vw',
        padding: '16px',
      }
    : {
        background: '#0f1219',
        color: '#f4f5f7',
        boxSizing: 'border-box',
        width: 'min(680px, 90vw)',
        maxWidth: '90vw',
      }

  React.useEffect(() => {
    if (!open) return
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose && onClose()
    }
    window.addEventListener('keydown', onEsc)
    return () => window.removeEventListener('keydown', onEsc)
  }, [open, onClose])

  return (
    <Modal
      open={!!open}
      lite
      overlayClassName={styles.overlay}
      contentClassName={contentClasses}
      contentStyle={contentStyle}
      contentProps={{ 'data-testid': 'help-overlay' } as React.ComponentPropsWithoutRef<'div'>}
      onClose={onClose}
    >
      {title && <ModalTitle className={styles.modalTitle}>{title}</ModalTitle>}

      {preAvatar}

      <div className={styles.avatarWrap}>
        <img src={char.img} alt={getHelpCharacterAlt(t, char.id)} className={styles.avatarImg} />
      </div>

      {noTextWrap ? (
        children
      ) : (
        <Text variant="legend" className={styles.legend}>
          {children}
        </Text>
      )}

      <FormLine className={styles.okLine}>
        <Button type="button" onClick={onClose} className={styles.okButton}>
          {t('common.ok')}
        </Button>
      </FormLine>
    </Modal>
  )
}
